import { FaLinkedin, FaFacebook, FaInstagram, FaYoutube } from "react-icons/fa";
import { BsTwitterX } from "react-icons/bs";

export type SiteConfig = typeof siteConfig;

// Locale-specific site configurations
const siteConfigs = {
  en: {
    name: "Faxify: Send & Receive Fax | For iPhone, iPad, Android & Web",
    description:
      "Send and receive faxes online from any device. Get your dedicated fax number and secure cloud storage. No fax machine required.",
  },
  es: {
    name: "Faxify: Enviar y Recibir Fax | Para iPhone, iPad, Android y Web",
    description:
      "Envía y recibe faxes online desde cualquier dispositivo. Obtén tu número de fax dedicado y almacenamiento seguro en la nube. Sin máquina de fax requerida.",
  },
  fr: {
    name: "Faxify: Envoyer et Recevoir Fax | iPhone, iPad, Android & Web",
    description:
      "Envoyez et recevez des fax en ligne depuis n'importe quel appareil. Obtenez votre numéro de fax dédié et un stockage cloud sécurisé. Aucune machine de fax requise.",
  },
};

// Function to get locale-specific site configuration
export function getSiteConfig(locale: string = "en") {
  const config =
    siteConfigs[locale as keyof typeof siteConfigs] || siteConfigs.en;

  return {
    ...siteConfig,
    name: config.name,
    description: config.description,
  };
}

export const siteConfig = {
  name: "Faxify: Send & Receive Fax | For iPhone, iPad, Android & Web",
  // Short brand = the SITE NAME Google shows in SERPs (og:site_name + the
  // WebSite schema `name`). Kept distinct from `name`, which is the long
  // homepage <title>. Locale-invariant — "Faxify" is the brand everywhere.
  shortName: "Faxify",
  description:
    "Send and receive faxes online from any device. Get your dedicated fax number and secure cloud storage. No fax machine required.",
  companyName: "Const Agility, LLC",

  /**
   * One-time Fax Pass tiers.
   *
   * SINGLE SOURCE for the marketing site — the pricing card, the Product/Offer
   * JSON-LD and any copy all read from here, so a reprice is one edit rather
   * than a hunt across pages, schema and llms.txt.
   *
   * WHY THESE ARE NOT LIVE-FETCHED like the subscription plans are. Passes are
   * not `Plan` rows; they exist only as RevenueCat offerings
   * (`fax_pass_25` / `fax_pass_100`, marked by `fax_pass_max_pages` metadata),
   * and RevenueCat's v2 offerings API does not return a price — price is only
   * reachable through a client-side RC SDK, which a static marketing site does
   * not carry. `maxPages` could be fetched; the price genuinely cannot.
   *
   * THE REAL SOURCE OF TRUTH IS THE REVENUECAT DASHBOARD. If a tier is
   * repriced or added there, change it here in the same sitting. Buyers are
   * charged the store price, never this number.
   */
  faxPass: {
    currency: "USD",
    tiers: [
      { maxPages: 25, price: 2.99 },
      { maxPages: 100, price: 4.99 },
    ],
  },
  url: "https://www.faxify.com",
  // Meta keywords carry no Google ranking weight; Bing reads them but its
  // spam classifier can penalize competitor brand names. The list below is
  // intentionally broad for product/feature/AI coverage, with competitor
  // brand names ("efax", "fax plus", "fax.plus") deliberately excluded.
  keywords: [
    "send fax online",
    "receive fax online",
    "fax service",
    "online fax",
    "digital fax",
    "fax app",
    "mobile fax",
    "fax from phone",
    "fax from computer",
    "secure fax",
    "fax without machine",
    "fax software",
    "cloud fax",
    "electronic fax",
    "fax solution",
    "send fax",
    "receive fax",
    "free fax",
    "how to send fax from phone",
    "how to send fax from iphone",
    "how to send fax from ipad",
    "dedicated fax number",
    "personal fax number",
    "business fax number",
    "fax number for small business",
    "affordable fax",
    "cheap fax",
    "easy fax",
    "simple fax",
    "AI",
    "Generative AI",
    "Gen AI",
    "ChatGPT",
    "Claude",
    "Cursor",
    "MCP",
    "Model Context Protocol",
    "Connector",
    "AI Connector",
    "MCP Connector",
    "AI fax",
    "AI fax service",
    "fax with AI",
    "AI-powered fax",
    "fax from AI",
    "send fax from AI",
    "AI tools fax",
    "ChatGPT fax",
    "Claude fax",
    "Cursor fax",
    "MCP fax",
    "fax MCP",
    "fax connector",
    "AI integration fax",
  ],
  author: "Const Agility, LLC",
  company: {
    name: "Const Agility, LLC",
    address: "17350 State Hwy 249, Ste 220",
    city: "Houston, TX 77064, USA",
    addressLocality: "Houston",
    addressRegion: "TX",
    postalCode: "77064",
    phone: "+1 (945) 398-0695",
    phoneLink: "tel:+19453980695",
    email: "hello@faxify.com",
    emailLink: "mailto:hello@faxify.com",
    fullAddress: "17350 State Hwy 249, Ste 220, Houston, TX 77064, USA",
  },
  navItems: [
    {
      label: "Home",
      href: "/",
    },
    {
      label: "MCP",
      href: "/mcp",
    },
    {
      label: "Blogs",
      href: "/blogs",
    },
    {
      label: "Pricing",
      href: "/pricing",
    },
    {
      label: "FAQ",
      href: "/faq",
    },
    {
      label: "About",
      href: "/about",
    },
  ],
  footerLinks: [
    {
      href: "/privacy.html",
      key: "Privacy",
      trackingLabel: "Privacy",
    },
    {
      href: "/terms.html",
      key: "Terms",
      trackingLabel: "Terms",
    },
  ],
  // twitter:site / twitter:creator want the @handle, not the profile URL.
  twitterHandle: "@faxify_app",
  // RSS autodiscovery. Every page sets `alternates` itself and Next replaces
  // (does not merge) nested metadata objects, so pages spread this in.
  rssAlternate: {
    "application/rss+xml": [
      { url: "/feed.xml", title: "Faxify Blog RSS Feed" },
    ],
  },
  socials: [
    {
      href: "https://www.linkedin.com/company/faxify",
      icon: FaLinkedin,
      label: "LinkedIn",
      trackingLabel: "LinkedIn",
    },
    {
      href: "https://x.com/faxify_app",
      icon: BsTwitterX,
      label: "X (Twitter)",
      trackingLabel: "X",
    },
    {
      href: "https://www.facebook.com/profile.php?id=100093169552064",
      icon: FaFacebook,
      label: "Facebook",
      trackingLabel: "Facebook",
    },
    {
      href: "https://www.instagram.com/faxify_app",
      icon: FaInstagram,
      label: "Instagram",
      trackingLabel: "Instagram",
    },
    {
      href: "https://www.youtube.com/@faxify_app",
      icon: FaYoutube,
      label: "YouTube",
      trackingLabel: "YouTube",
    },
  ],
  appStoreLinks: {
    apple:
      "https://apps.apple.com/us/app/faxify-fax-app-from-iphone/id6444380905",
    google:
      "https://play.google.com/store/apps/details?id=com.constagility.faxify",
  },
  appUrl: "https://app.faxify.com",
};
